import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	TFileId,
	IGetDishInfoV2,
	TLanguageIDs,
	IItemForGetShortInfo,
	IDefaultLanguage,
	IUpdateDishV2,
	IItemForGetShortDishInfo,
} from 'types';
import { editDishInitialState, EditDishContext, TTypeOfModalWindow } from './editDishContext';
import { editDishReducer } from './editDishReducer';
import { EditDishActionTypes } from './editDishActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getDishInfoV2 } from 'backend/dishes';
import { v4 as uuidv4 } from 'uuid';

export const EditDishState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editDishReducer, editDishInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const dishID = params.get('dishID');

	const { response, loading, refetch } = useFetchData<IGetDishInfoV2, string>(
		dishID ? getDishInfoV2 : null,
		dishID || '',
	);

	useNamedHeader(response?.data.name);

	useEffect(() => {
		if (response) {
			const translations = response.data.translations.map((translation) => ({
				languageId: translation.languageId,
				name: translation.name,
				description: translation.description,
			}));

			dispatch({
				type: EditDishActionTypes.SET_ALL_DATA,
				payload: {
					dishID: dishID || '',
					type: response.data.type,
					isCreatedOnPos: response.data.isCreatedOnPos,
					filePath: response.data.photoPath,
					nameOfDish: response.data.name,
					description: response.data.description || '',
					initialIDSelectedCustomer: response.data.customer.id,
					posName: response.data.posName || '',
					externalId: response.data.externalId || '',
					articleNumber: response.data.articleNumber || '',
					initialIDsSelectedRestaurant: response.data.restaurants.map((restaurant) => restaurant.id),
					hasSchedulePriority: response.data.overrideScheduleAvailability,
					activationSchedule: response.data.activationCron || '',
					deactivationSchedule: response.data.deactivationCron || '',
					translations,
					fetchedSuggestedDishes: response.data.suggestedDishes.map((suggestedDish) => ({
						...suggestedDish,
						externalId: null,
					})),
					selectedSuggestedDishes: response.data.suggestedDishes.map((suggestedDish) => suggestedDish.id),
					fetchedAdditionalDishes: response.data.additionalDishes.map((additionalDish) => ({
						...additionalDish,
						externalId: null,
					})),
					selectedAdditionalDishes: response.data.additionalDishes.map((additionalDish) => additionalDish.id),
					fetchedModificationDishes: response.data.dishModifications.map((dishModification) => ({
						...dishModification,
						externalId: null,
					})),
					selectedModificationDishes: response.data.dishModifications.map(
						(dishModification) => dishModification.id,
					),
				},
			});
		}
	}, [dishID, response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditDishActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditDishActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditDishActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditDishActionTypes.DELETE_PHOTO }), []);

	const changeNameOfDish = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_NAME_OF_DISH,
			payload: {
				nameOfDish: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const changePosName = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_POS_NAME,
			payload: {
				posName: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: EditDishActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const changeArticleNumber = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_ARTICLE_NUMBER,
			payload: {
				articleNumber: value,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditDishActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: EditDishActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditDishActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: EditDishActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const toggleHasSchedulePriority = useCallback(() => {
		dispatch({
			type: EditDishActionTypes.TOGGLE_HAS_SCHEDULE_PRIORITY,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback(({ value, isInitialSet }: { value: string[]; isInitialSet: boolean }) => {
		dispatch({
			type: isInitialSet
				? EditDishActionTypes.SET_INITIAL_IDS_RESTAURANT
				: EditDishActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				fetchedCategories: value,
			},
		});
	}, []);

	const setSuggestedDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_SUGGESTED_DISH_CATEGORY,
			payload: {
				selectedSuggestedDishCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_SUGGESTED_DISH_SUB_CATEGORIES,
			payload: {
				fetchedSuggestedDishSubCategories: value,
			},
		});
	}, []);

	const setSuggestedDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_SUGGESTED_DISH_SUB_CATEGORY,
			payload: {
				selectedSuggestedDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedSuggestedDishes.includes(el.id));
			const filteredFetchedSuggestedDishes = state.fetchedSuggestedDishes.filter((el) =>
				state.selectedSuggestedDishes.includes(el.id),
			);

			dispatch({
				type: EditDishActionTypes.SET_FETCHED_SUGGESTED_DISHES,
				payload: {
					fetchedSuggestedDishes: [...filteredValue, ...filteredFetchedSuggestedDishes],
				},
			});
		},
		[state.selectedSuggestedDishes, state.fetchedSuggestedDishes],
	);

	const setSuggestedDishes = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_SUGGESTED_DISHES,
			payload: {
				selectedSuggestedDishes: value,
			},
		});
	}, []);

	const removeSuggestedDish = useCallback(
		(value: string) => {
			const updatedSelectedSuggestedDishes = state.selectedSuggestedDishes.filter((el) => el !== value);

			dispatch({
				type: EditDishActionTypes.SET_SUGGESTED_DISHES,
				payload: {
					selectedSuggestedDishes: updatedSelectedSuggestedDishes,
				},
			});
		},
		[state.selectedSuggestedDishes],
	);

	const setAdditionalDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_ADDITIONAL_DISH_CATEGORY,
			payload: {
				selectedAdditionalDishCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_ADDITIONAL_DISH_SUB_CATEGORIES,
			payload: {
				fetchedAdditionalDishSubCategories: value,
			},
		});
	}, []);

	const setAdditionalDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_ADDITIONAL_DISH_SUB_CATEGORY,
			payload: {
				selectedAdditionalDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedAdditionalDishes.includes(el.id));
			const filteredFetchedAdditionalDishes = state.fetchedAdditionalDishes.filter((el) =>
				state.selectedAdditionalDishes.includes(el.id),
			);

			dispatch({
				type: EditDishActionTypes.SET_FETCHED_ADDITIONAL_DISHES,
				payload: {
					fetchedAdditionalDishes: [...filteredValue, ...filteredFetchedAdditionalDishes],
				},
			});
		},
		[state.selectedAdditionalDishes, state.fetchedAdditionalDishes],
	);

	const setAdditionalDishes = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_ADDITIONAL_DISHES,
			payload: {
				selectedAdditionalDishes: value,
			},
		});
	}, []);

	const removeAdditionalDish = useCallback(
		(value: string) => {
			const updatedSelectedAdditionalDishes = state.selectedAdditionalDishes.filter((el) => el !== value);

			dispatch({
				type: EditDishActionTypes.SET_ADDITIONAL_DISHES,
				payload: {
					selectedAdditionalDishes: updatedSelectedAdditionalDishes,
				},
			});
		},
		[state.selectedAdditionalDishes],
	);

	const setModificationDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_MODIFICATION_DISH_CATEGORY,
			payload: {
				selectedModificationDishCategory: value,
			},
		});
	}, []);

	const setFetchedModificationDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_MODIFICATION_DISH_SUB_CATEGORIES,
			payload: {
				fetchedModificationDishSubCategories: value,
			},
		});
	}, []);

	const setModificationDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_MODIFICATION_DISH_SUB_CATEGORY,
			payload: {
				selectedModificationDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedModificationDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedModificationDishes.includes(el.id));
			const filteredFetchedModificationDishes = state.fetchedModificationDishes.filter((el) =>
				state.selectedModificationDishes.includes(el.id),
			);

			dispatch({
				type: EditDishActionTypes.SET_FETCHED_MODIFICATION_DISHES,
				payload: {
					fetchedModificationDishes: [...filteredValue, ...filteredFetchedModificationDishes],
				},
			});
		},
		[state.selectedModificationDishes, state.fetchedModificationDishes],
	);

	const setModificationDishes = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_MODIFICATION_DISHES,
			payload: {
				selectedModificationDishes: value,
			},
		});
	}, []);

	const removeModificationDish = useCallback(
		(value: string) => {
			const updatedSelectedModificationDishes = state.selectedModificationDishes.filter((el) => el !== value);

			dispatch({
				type: EditDishActionTypes.SET_MODIFICATION_DISHES,
				payload: {
					selectedModificationDishes: updatedSelectedModificationDishes,
				},
			});
		},
		[state.selectedModificationDishes],
	);

	const setDataForSubmit = useCallback((value: Nullable<IUpdateDishV2>) => {
		dispatch({
			type: EditDishActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<EditDishContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				setFileId,
				deletePhoto,
				changeNameOfDish,
				changeDescription,
				setFetchedCustomers,
				setIDCustomer,
				changePosName,
				changeExternalId,
				generateUniqueExternalId,
				changeArticleNumber,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				toggleHasSchedulePriority,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedRestaurants,
				setIDsRestaurant,
				setFetchedCategories,
				setSuggestedDishCategory,
				setFetchedSuggestedDishSubCategories,
				setSuggestedDishSubCategory,
				setFetchedSuggestedDishes,
				setSuggestedDishes,
				removeSuggestedDish,
				setAdditionalDishCategory,
				setFetchedAdditionalDishSubCategories,
				setAdditionalDishSubCategory,
				setFetchedAdditionalDishes,
				setAdditionalDishes,
				removeAdditionalDish,
				setModificationDishCategory,
				setFetchedModificationDishSubCategories,
				setModificationDishSubCategory,
				setFetchedModificationDishes,
				setModificationDishes,
				removeModificationDish,
				setDataForSubmit,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				isLoading: state.isLoading,
				filePath: state.filePath,
				fileId: state.fileId,
				dishID: state.dishID,
				type: state.type,
				isCreatedOnPos: state.isCreatedOnPos,
				nameOfDish: state.nameOfDish,
				description: state.description,
				fetchedCustomers: state.fetchedCustomers,
				initialIDSelectedCustomer: state.initialIDSelectedCustomer,
				IDSelectedCustomer: state.IDSelectedCustomer,
				posName: state.posName,
				externalId: state.externalId,
				articleNumber: state.articleNumber,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				fetchedRestaurants: state.fetchedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				fetchedCategories: state.fetchedCategories,
				selectedSuggestedDishCategory: state.selectedSuggestedDishCategory,
				fetchedSuggestedDishSubCategories: state.fetchedSuggestedDishSubCategories,
				selectedSuggestedDishSubCategory: state.selectedSuggestedDishSubCategory,
				fetchedSuggestedDishes: state.fetchedSuggestedDishes,
				selectedSuggestedDishes: state.selectedSuggestedDishes,
				selectedAdditionalDishCategory: state.selectedAdditionalDishCategory,
				fetchedAdditionalDishSubCategories: state.fetchedAdditionalDishSubCategories,
				selectedAdditionalDishSubCategory: state.selectedAdditionalDishSubCategory,
				fetchedAdditionalDishes: state.fetchedAdditionalDishes,
				selectedAdditionalDishes: state.selectedAdditionalDishes,
				selectedModificationDishCategory: state.selectedModificationDishCategory,
				fetchedModificationDishSubCategories: state.fetchedModificationDishSubCategories,
				selectedModificationDishSubCategory: state.selectedModificationDishSubCategory,
				fetchedModificationDishes: state.fetchedModificationDishes,
				selectedModificationDishes: state.selectedModificationDishes,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditDishContext.Provider>
	);
};
